import { ComponentProps, ComponentType, useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { RadioInputField } from '../RadioInputField';
import { RadioCheckboxCardField } from '../RadioCheckboxCardField';
import { DropdownValueArrayCard } from '../DropdownValueArrayCard';
import {
  ServerTypeRadioFieldUiOptions,
  ServerTypeRadioFieldValue,
} from './schema';

/** Top-level selection: remote (hosted) or local MCP server. */
type ServerDeploymentModel = ServerTypeRadioFieldValue['serverDeploymentModel'];
type Configurations = NonNullable<ServerTypeRadioFieldValue['configurations']>;

/**
 * Backstage Scaffolder custom field extension — use in a template.yaml with
 * ui:field: ServerTypeRadioField.
 *
 * Renders "Remote MCP Server" / "Local MCP Server" radio buttons. Remote shows
 * a server URL input plus an embedded auth card (RadioInputField, or
 * RadioCheckboxCardField when remoteAuthVariant is "checkboxCard"). Local
 * shows a nested array of typed configuration content.
 * Returns a nested object with serverDeploymentModel and the selected
 * branch's data.
 *
 * @param props FieldExtensionComponentProps injected by the Scaffolder
 * @returns ServerTypeRadioField React Element
 */
export const ServerTypeRadioField = (
  props: FieldExtensionComponentProps<
    ServerTypeRadioFieldValue,
    ServerTypeRadioFieldUiOptions
  >,
) => {
  const {
    onChange,
    formData,
    formContext,
    uiSchema,
    schema: { title, description },
    disabled,
    readonly,
  } = props;

  const options = uiSchema['ui:options'];
  const remoteLabel = options?.remoteLabel ?? 'Remote MCP Server';
  const localLabel = options?.localLabel ?? 'Local MCP Server';
  const remoteAuthVariant = options?.remoteAuthVariant ?? 'radio';
  const remoteOptions = options?.remoteOptions ?? [];
  const serverUrlLabel = options?.serverUrlLabel ?? 'MCP Server URL';
  const serverUrlPlaceholder = options?.serverUrlPlaceholder;
  const remoteCardTitle = options?.remoteCardTitle;
  const remoteCardDescription = options?.remoteCardDescription;
  const localCardTitle = options?.localCardTitle;
  const localCardDescription = options?.localCardDescription;

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const backgroundColor = theme.palette.background.default;
  const nestedCardColor = isDark ? '#0D0D0D' : '#FFFFFF';

  const isEditMode = disabled || readonly;
  const lockSelection = options?.lockSelection ?? false;
  const isSelectionLocked = isEditMode || lockSelection;

  const hideAuthorizationRule = options?.hideAuthorizationWhen ?? {
    field: 'serverType',
    equals: 'Public',
  };
  const hideAuthorization =
    (formContext?.formData as Record<string, unknown> | undefined)?.[
      hideAuthorizationRule.field
    ] === hideAuthorizationRule.equals;

  const disableRemoteRule = options?.disableRemoteWhen;
  const remoteDisabled =
    isSelectionLocked ||
    (disableRemoteRule !== undefined &&
      (formContext?.formData as Record<string, unknown> | undefined)?.[
        disableRemoteRule.field
      ] === disableRemoteRule.equals);

  const serverDeploymentModel: ServerDeploymentModel =
    formData?.serverDeploymentModel ?? 'remote';
  const configurationsSchema =
    (props.schema as any)?.properties?.configurations ?? {};
  const configurationsUiSchema = (uiSchema as any)?.configurations ?? {};

  /**
   * When remote is disabled and the current selection is remote, switch to local
   */
  useEffect(() => {
    if (
      remoteDisabled &&
      !isSelectionLocked &&
      formData?.serverDeploymentModel === 'remote'
    ) {
      onChange({
        serverDeploymentModel: 'local',
        configurations: formData.configurations?.length
          ? formData.configurations
          : [{ type: '', jsonFile: '' }],
      });
    }
  }, [
    remoteDisabled,
    isSelectionLocked,
    formData?.serverDeploymentModel,
    formData?.configurations,
    onChange,
  ]);

  /**
   * Set default value to remote if not set
   */
  useEffect(() => {
    if (!formData?.serverDeploymentModel) {
      onChange({ serverDeploymentModel: 'remote' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hideAuthorization && formData?.authorization) {
      const { authorization: _authorization, ...rest } = formData;
      onChange(rest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideAuthorization]);

  const handleDeploymentModelChange = (value: ServerDeploymentModel) => {
    if (isSelectionLocked) return;
    onChange(
      value === 'remote'
        ? { serverDeploymentModel: 'remote' }
        : {
            serverDeploymentModel: 'local',
            configurations: [{ type: '', jsonFile: '' }],
          },
    );
  };

  const handleAuthorizationChange = (authorization: Record<string, any>) => {
    onChange({
      serverDeploymentModel: 'remote',
      serverUrl: formData?.serverUrl,
      authorization,
    });
  };

  const handleConfigurationsChange = (configurations: Configurations) => {
    onChange({
      serverDeploymentModel: 'local',
      configurations,
    });
  };

  const AuthCard: ComponentType<any> =
    remoteAuthVariant === 'checkboxCard'
      ? RadioCheckboxCardField
      : RadioInputField;

  const authCardProps = {
    ...props,
    disabled:
      remoteAuthVariant === 'checkboxCard' ? isSelectionLocked : isEditMode,
    formData: formData?.authorization,
    uiSchema: { 'ui:options': { options: remoteOptions } },
    schema: {
      title: remoteCardTitle ?? '',
      description: remoteCardDescription,
    },
    rawErrors: undefined,
    onChange: handleAuthorizationChange,
  } as unknown as ComponentProps<typeof RadioInputField>;

  return (
    <Box sx={{ backgroundColor, padding: '1rem', borderRadius: '0.25rem' }}>
      {title && (
        <Typography
          variant="h6"
          sx={{
            marginBottom: 1,
            marginTop: 0,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
      )}

      {description && (
        <Typography
          variant="body2"
          sx={{
            marginBottom: 1,
            marginTop: 0,
            color: theme.palette.text.primary,
          }}
        >
          {description}
        </Typography>
      )}

      {(title || description) && (
        <Divider sx={{ marginBottom: 2, marginTop: 0.5 }} />
      )}

      <RadioGroup
        row
        value={serverDeploymentModel}
        onChange={e =>
          handleDeploymentModelChange(e.target.value as ServerDeploymentModel)
        }
      >
        <FormControlLabel
          value="remote"
          control={<Radio disabled={isSelectionLocked || remoteDisabled} />}
          label={remoteLabel}
        />
        <FormControlLabel
          value="local"
          control={<Radio disabled={isSelectionLocked} />}
          label={localLabel}
        />
      </RadioGroup>

      {serverDeploymentModel === 'remote' ? (
        <Box sx={{ mt: 2 }}>
          <TextField
            label={serverUrlLabel}
            required
            fullWidth
            variant="outlined"
            size="medium"
            disabled={isSelectionLocked}
            placeholder={serverUrlPlaceholder}
            value={formData?.serverUrl ?? ''}
            onChange={e => {
              const serverUrl = e.target.value;
              onChange({
                ...formData,
                serverDeploymentModel: 'remote',
                serverUrl,
              });
            }}
          />
          {!hideAuthorization && (
            <Box
              sx={{
                mt: 2,
                '& > div': {
                  backgroundColor: `${theme.palette.background.paper}`,
                },
              }}
            >
              <AuthCard {...authCardProps} />
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            mt: 2,
            backgroundColor: nestedCardColor,
            padding: '1rem',
            borderRadius: '0.25rem',
          }}
        >
          {localCardTitle && (
            <Typography
              variant="h6"
              sx={{ marginBottom: 1, color: theme.palette.text.primary }}
            >
              {localCardTitle}
            </Typography>
          )}

          {localCardDescription && (
            <Typography
              variant="body2"
              sx={{ marginBottom: 1, color: theme.palette.text.primary }}
            >
              {localCardDescription}
            </Typography>
          )}

          <DropdownValueArrayCard
            {...(props as any)}
            schema={configurationsSchema}
            uiSchema={configurationsUiSchema}
            formData={formData?.configurations}
            onChange={handleConfigurationsChange}
            disabled={isEditMode}
            readonly={readonly}
            idSchema={{
              ...props.idSchema,
              $id: `${props.idSchema.$id}_configurations`,
            }}
            rawErrors={undefined}
          />
        </Box>
      )}
    </Box>
  );
};
